import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/api/client';

/**
 * Sign-in for staff accounts (ASP.NET Identity, cookie scheme). There's no "create account"
 * link on purpose — registration is closed to anonymous callers, so accounts come from the
 * seeded admin adding colleagues.
 */
export function SignIn() {
  const queryClient = useQueryClient();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');

  const signIn = useMutation({
    mutationFn: () => api.login(email.trim(), password),
    // Reset rather than clear: both drop the cache the anonymous caller populated, but only
    // resetQueries refetches the mounted queries — the `me` gate flips to the console
    // immediately instead of waiting for a manual refresh.
    onSuccess: () => queryClient.resetQueries(),
  });

  const submit = (event: React.FormEvent) => {
    event.preventDefault();
    if (email.trim() && password) signIn.mutate();
  };

  return (
    <div className="flex min-h-screen items-center justify-center px-6">
      <form onSubmit={submit} className="panel w-full max-w-sm p-6">
        <h1 className="text-lg font-semibold tracking-tight">
          SUPER<span className="text-emerald-400">TRON</span>
        </h1>
        <p className="mt-1 mb-5 text-sm text-zinc-400">Sign in to the operator console.</p>

        <label className="mb-3 block">
          <span className="field-label">Email</span>
          <input
            className="field-input font-sans"
            type="email"
            autoComplete="username"
            value={email}
            onChange={(event) => setEmail(event.target.value)}
          />
        </label>

        <label className="block">
          <span className="field-label">Password</span>
          <input
            className="field-input font-sans"
            type="password"
            autoComplete="current-password"
            value={password}
            onChange={(event) => setPassword(event.target.value)}
          />
        </label>

        {signIn.isError && (
          <p className="mt-3 text-xs text-rose-300">
            {/* Identity answers a bad password with a bare 401, so say something useful. */}
            {(signIn.error as Error).message === 'Sign-in required.'
              ? 'Wrong email or password.'
              : (signIn.error as Error).message}
          </p>
        )}

        <button
          type="submit"
          className="btn btn-primary mt-5 w-full justify-center"
          disabled={!email.trim() || !password || signIn.isPending}
        >
          {signIn.isPending ? 'Signing in…' : 'Sign in'}
        </button>
      </form>
    </div>
  );
}
